import { yupResolver } from '@hookform/resolvers/yup';
import { LoadingButton } from '@mui/lab';
import { Box, CircularProgress, Container, Link, TextField, Typography } from '@mui/material';
import { HttpStatusCode } from 'axios';
import { ServerError } from '@src/common-utils/AuthModels';
import { Nullable } from '@src/common-utils/Models';
import { useUserContext } from '@src/contexts/UserContextProvider';
import { ROUTES } from '@src/layout/RoutePage';
import { useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import * as yup from 'yup';
import ReCAPTCHA from 'react-google-recaptcha';
import envConfig from '@src/env';

const resendConfirmationSchema = yup.object().shape({
  email: yup.string().email().required('Email is required'),
});

type ResendConfirmationFormData = {
  email: string;
};

export const ResendConfirmation = () => {
  const { isLoading, resendConfirmationCode } = useUserContext();
  const { state } = useLocation();
  const navigate = useNavigate();

  const [message, setMessage] = useState<Nullable<string>>(null);
  const [error, setError] = useState<Nullable<Error>>(null);
  const [confirmedEmail, setConfirmedEmail] = useState<Nullable<string>>(null);
  const [recaptchaValue, setRecaptchaValue] = useState<Nullable<string>>(null);
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<ResendConfirmationFormData>({ resolver: yupResolver(resendConfirmationSchema) });

  // A reCAPTCHA token is single-use: once the API has verified it, force a fresh solve before any retry.
  const resetRecaptcha = () => {
    recaptchaRef.current?.reset();
    setRecaptchaValue(null);
  };

  const onSubmit = async (data: ResendConfirmationFormData) => {
    setConfirmedEmail(null);
    if (!recaptchaValue) {
      setMessage(null);
      setError(new Error('Please complete the reCAPTCHA check to provide that you are not a robot'));
      return;
    }

    await resendConfirmationCode({
      email: data.email,
      recaptchaToken: recaptchaValue,
      onSuccess: () => {
        resetRecaptcha();
        setError(null);
        setMessage(`A new verification email was sent to ${data.email}.`);
      },
      onError: (error: Error) => {
        resetRecaptcha();
        setMessage(null);
        if (error instanceof ServerError && error.statusCode === HttpStatusCode.Conflict) {
          setError(null);
          setConfirmedEmail(data.email);
          return;
        }
        setError(error);
      },
    });
  };

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: 4,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Typography component="h1" variant="h5" align="center" sx={{ color: 'primary', mb: '8px' }}>
          Resend confirmation email
        </Typography>
        <Typography component="span" variant="body2">
          Signed up but never confirmed your email? Enter your email address and we'll send a new confirmation code.
        </Typography>
      </Box>
      <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate sx={{ mt: 1 }}>
        <Controller
          name="email"
          control={control}
          defaultValue={state?.email ?? ''}
          render={({ field }) => (
            <TextField
              {...field}
              margin="normal"
              required
              fullWidth
              label="Email Address"
              autoComplete="email"
              autoFocus
              error={Boolean(errors.email)}
              helperText={errors.email?.message || ''}
            />
          )}
        />
        {message && (
          <Typography component="h4" variant="body2" sx={{ color: 'blue' }}>
            {message}
          </Typography>
        )}
        {error && (
          <Typography component="h4" variant="body2" color="error">
            {error.message}
          </Typography>
        )}
        {confirmedEmail && (
          <Typography component="h4" variant="body2">
            This account is already confirmed.{' '}
            <Link component="button" type="button" variant="body2" onClick={() => navigate(ROUTES.signin)}>
              Sign in
            </Link>
            , or{' '}
            <Link
              component="button"
              type="button"
              variant="body2"
              onClick={() => navigate(ROUTES.forgotPassowrd, { state: { email: confirmedEmail } })}
            >
              reset your password
            </Link>{' '}
            if you've forgotten it.
          </Typography>
        )}
        <ReCAPTCHA
          ref={recaptchaRef}
          sitekey={envConfig.reCAPTCHASiteKey}
          onChange={setRecaptchaValue}
          onExpired={() => setRecaptchaValue(null)}
        />
        <LoadingButton
          loading={isLoading}
          loadingPosition="end"
          type="submit"
          fullWidth
          variant="contained"
          sx={{ mt: 3, mb: 2, textTransform: 'none' }}
          endIcon={<CircularProgress size={isLoading ? 20 : 0} />}
        >
          Send confirmation email
        </LoadingButton>
      </Box>
    </Container>
  );
};
